import React, { createContext, useContext } from 'react';
import { usePropertyForm } from '../hooks/usePropertyForm';
import type { PropertyFormData } from '../hooks/usePropertyForm';
import { Property } from '../../../../types/property';
import { usePhotoContext } from './PhotoContext';
import { conditions } from '../constants/propertyOptions';

// Helper pour normaliser les valeurs d'état général
const normalizeCondition = (value: string | null | undefined): string => {
  if (!value) return '';
  // Convertir en minuscules pour la comparaison
  const lowerValue = value.toLowerCase();
  // Trouver la valeur correspondante dans les constantes (avec majuscule correcte)
  const matchedCondition = conditions.find(c => c.toLowerCase() === lowerValue);
  return matchedCondition || value;
};

interface PropertyFormContextType {
  formData: PropertyFormData;
  property: Property | undefined;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  updateFormData: (updates: Partial<PropertyFormData>) => void;
  handleCopyAddress: () => void;
  getPhotosForSave: () => string[];
}

export const PropertyFormContext = createContext<PropertyFormContextType | undefined>(undefined);

interface PropertyFormProviderProps {
  children: React.ReactNode;
  initialTransactionType?: PropertyFormData['transactionType'];
  initialProperty?: Property;
}

export const PropertyFormProvider: React.FC<PropertyFormProviderProps> = ({
  children,
  initialTransactionType = '',
  initialProperty
}) => {
  const formMethods = usePropertyForm();

  // ✅ Déclaration de getPhotosForSave avant son utilisation
  const getPhotosForSave = () => {
    // Cette fonction sera appelée par les composants enfants pour récupérer les photos
    return [];
  };

  // ✅ Utilisation de getPhotosForSave dans contextValue
  const contextValue = {
    ...formMethods,
    property: initialProperty,
    getPhotosForSave
  };

  // Initialiser le type de transaction si fourni
  React.useEffect(() => {
    if (initialTransactionType && initialTransactionType !== '') {
      formMethods.updateFormData({ transactionType: initialTransactionType as any });
    }
  }, [initialTransactionType]);

  // Initialiser les données du formulaire avec la propriété existante
  React.useEffect(() => {
    if (initialProperty) {
      console.log('🔄 Initialisation du formulaire avec la propriété:', initialProperty.id, 'Photos:', initialProperty.photos);

      // Déterminer le transactionType basé sur le status
      const getTransactionTypeFromStatus = (status: string) => {
        switch (status) {
          case 'for-sale':
          case 'sold':
            return 'sale';
          case 'for-rent':
          case 'rented':
            return 'rent';
          case 'for-short-rent':
            return 'short-rent';
          case 'estimation':
            return 'estimation';
          default:
            return 'sale';
        }
      };

      const propertyData: Partial<PropertyFormData> = {
        // Type de transaction (dérivé du status)
        transactionType: getTransactionTypeFromStatus(initialProperty.status || 'for-sale'),

        // Informations du propriétaire
        ownerCivility: initialProperty.owner?.name?.split(' ')[0] || initialProperty.owner_name?.split(' ')[0] || '',
        ownerFirstName: initialProperty.owner?.name?.split(' ').slice(1, -1).join(' ') || initialProperty.owner_name?.split(' ').slice(1, -1).join(' ') || '',
        ownerLastName: initialProperty.owner?.name?.split(' ').slice(-1)[0] || initialProperty.owner_name?.split(' ').slice(-1)[0] || '',
        ownerAddress: initialProperty.owner_address || '',
        ownerWilaya: initialProperty.owner_wilaya || '',
        ownerCity: initialProperty.owner_city || '',
        ownerPhone: initialProperty.owner?.phone || initialProperty.owner_phone || '',
        ownerEmail: initialProperty.owner?.email || initialProperty.owner_email || '',

        // Informations du bien
        propertyType: initialProperty.property_category || '',
        apartmentType: initialProperty.property_subtype || '',
        propertyAddress: initialProperty.address || '',
        propertyCity: initialProperty.city || '',
        propertyWilaya: initialProperty.wilaya || '',
        reference: initialProperty.reference_number || '',

        // Prix
        sellerPrice: initialProperty.net_price?.toString() || initialProperty.netPrice?.toString() || '',
        agencyCommission: initialProperty.commission?.toString() || '',
        salePrice: initialProperty.price?.toString() || '',

        // Caractéristiques
        livingArea: initialProperty.area?.toString() || initialProperty.features?.area?.toString() || '',
        livingRoomArea: initialProperty.living_room_area?.toString() || '',
        usableArea: initialProperty.usable_area?.toString() || '',
        roomCount: initialProperty.rooms?.toString() || initialProperty.features?.rooms?.toString() || '',
        bedroomCount: initialProperty.bedrooms?.toString() || '',
        bathroomCount: initialProperty.bathroom_count?.toString() || initialProperty.bathrooms?.toString() || '1',
        floor: initialProperty.floor?.toString() || initialProperty.features?.floor?.toString() || '',
        currentFloor: initialProperty.current_floor?.toString() || '',
        totalFloors: initialProperty.total_floors?.toString() || '',
        isGroundFloor: initialProperty.is_ground_floor || false,
        isTopFloor: initialProperty.is_top_floor || false,
        constructionYear: initialProperty.construction_year?.toString() || initialProperty.features?.constructionYear?.toString() || '',
        condition: normalizeCondition(initialProperty.general_condition || initialProperty.condition),
        generalCondition: normalizeCondition(initialProperty.general_condition || initialProperty.condition),
        kitchenType: initialProperty.kitchen_type || '',
        view: initialProperty.view || '',
        // Log pour debug
        ...(console.log('🔍 [CONTEXT] Initialisation des champs critiques:', {
          general_condition_db: initialProperty.general_condition,
          general_condition_normalized: normalizeCondition(initialProperty.general_condition || initialProperty.condition),
          living_room_area: initialProperty.living_room_area,
          usable_area: initialProperty.usable_area,
          total_floors: initialProperty.total_floors,
          kitchen_type: initialProperty.kitchen_type,
          bathrooms: initialProperty.bathrooms,
          has_roof_terrace: initialProperty.has_roof_terrace,
          has_gas_heating: initialProperty.has_gas_heating,
          has_private_parking: initialProperty.has_private_parking,
          has_digicode: initialProperty.has_digicode,
          has_cistern: initialProperty.has_cistern,
          has_water_tank: initialProperty.has_water_tank,
          has_double_glazing: initialProperty.has_double_glazing,
          has_electric_shutters: initialProperty.has_electric_shutters
        }), {}),

        // Équipements
        hasElevator: initialProperty.has_elevator || initialProperty.features?.hasElevator || false,
        hasParking: initialProperty.has_parking || initialProperty.features?.hasParking || false,
        hasBalcony: initialProperty.has_balcony || initialProperty.features?.hasBalcony || false,
        hasTerrace: initialProperty.has_terrace || initialProperty.features?.hasTerrace || false,
        hasRoofTerrace: initialProperty.has_roof_terrace || false,
        hasAirConditioning: initialProperty.has_air_conditioning || initialProperty.features?.hasAirConditioning || false,
        hasGasHeating: initialProperty.has_gas_heating || false,
        hasPrivateParking: initialProperty.has_private_parking || false,
        hasDigicode: initialProperty.has_digicode || false,
        hasCistern: initialProperty.has_cistern || false,
        hasWaterTank: initialProperty.has_water_tank || false,
        hasDoubleGlazing: initialProperty.has_double_glazing || false,
        hasElectricShutters: initialProperty.has_electric_shutters || false,
        hasGarden: initialProperty.has_garden || false,
        hasPool: initialProperty.has_pool || false,
        hasSeaView: initialProperty.has_sea_view || false,
        hasMountainView: initialProperty.has_mountain_view || false,
        hasGarage: initialProperty.has_garage || false,
        hasCellar: initialProperty.has_cellar || false,
        hasAttic: initialProperty.has_attic || false,
        hasGuardian: initialProperty.has_guardian || false,
        hasIntercom: initialProperty.has_intercom || false,
        hasInterphone: initialProperty.has_interphone || false,
        hasTrustee: initialProperty.has_trustee || false,
        hasAlarm: initialProperty.has_alarm || false,
        hasVideoSurveillance: initialProperty.has_video_surveillance || false,
        hasFiber: initialProperty.has_fiber || false,
        heatingType: initialProperty.heating_type || initialProperty.features?.heatingType || '',
        orientation: initialProperty.orientation || '',
        exposure: initialProperty.exposure || '',
        floorLevel: initialProperty.floor_level || '',
        buildingCondition: initialProperty.building_condition || '',
        accessibility: initialProperty.accessibility || '',
        energyClass: initialProperty.energy_class || '',
        co2Emission: initialProperty.co2_emission || '',

        // Description
        advertisingText: initialProperty.description || '',
        advertisingTitle: initialProperty.listing_title || '',
        listingTitle: initialProperty.listing_title || initialProperty.advertising_title || '',
        showcaseText: initialProperty.showcase_text || '',
        keyLocation: initialProperty.keys_location || '',
        propertyKeys: initialProperty.property_keys || '',
        observations: initialProperty.observations || '',

        // Agent
        agent: initialProperty.agent_id?.toString() || initialProperty.agent || '',
        fileDate: initialProperty.file_date || '',

        // Publication - initialiser avec les valeurs spécifiques de la base
        publishToDarDz: Boolean(initialProperty.publish_dar_dz || initialProperty.publishToDarDz),
        publishToOuedkniss: Boolean(initialProperty.publish_ouedkniss || initialProperty.publishToOuedkniss),
        publishExternalSite: Boolean(initialProperty.publish_external_site || initialProperty.publishExternalSite),

        // Conciergerie
        is_concierge: Boolean(initialProperty.is_concierge),

        // Log pour debug de l'initialisation
        ...(console.log('🔍 [CONTEXT] Valeur condition de la BDD:', {
          general_condition_raw: initialProperty.general_condition,
          condition_raw: initialProperty.condition,
          general_condition_normalized: normalizeCondition(initialProperty.general_condition || initialProperty.condition),
          condition_final: normalizeCondition(initialProperty.general_condition || initialProperty.condition)
        }), {})
      };

      formMethods.updateFormData(propertyData);

      // Initialiser les photos dans le contexte si elles existent
      if (initialProperty.photos && Array.isArray(initialProperty.photos) && initialProperty.photos.length > 0) {
        console.log('🖼️ Initialisation des photos existantes:', initialProperty.photos);
        // Cette partie sera gérée par le PhotoProvider lors de l'initialisation
      }
    }
  }, [initialProperty]);

  return (
    <PropertyFormContext.Provider value={contextValue}>
      {children}
    </PropertyFormContext.Provider>
  );
};

export const usePropertyFormContext = () => {
  const context = useContext(PropertyFormContext);
  if (!context) {
    throw new Error('usePropertyFormContext must be used within a PropertyFormProvider');
  }
  return context;
};
